import { TimeDurations } from '@fullstory/library-ui';
import { Display } from '@fullstory/proto/fs/services/webber/frontend/display';
import React, { useMemo } from 'react';
import { ChartSpacing, defaultSpacing } from '../../util/spacing';
import { GuidelineGroup } from '../GuidelineGroup';
import { getYAxisValues } from './utils';
import css from './index.module.scss';
interface Props {
  height: number;
  yValues: [string, number][];
  ruler?: boolean;
  rulerLength?: number;
  spacing?: ChartSpacing;
}

/**
 * Helper for generating the y-axis on charts.
 *
 * @param yScale Translation function from a Y value to the scaled y SVG pixel value.
 * @param yTicks The data values of the gridlines / ticks on the y-axis. This needs to be intelligently crafted
 * and should come from d3's ticks function.
 * @param display The defined display format, when applicable.
 * @param durationUnit The duration formatting config, when applicable.
 * @returns Array of values. The first value in each tuple is the formatted tick label, the second the scaled y value.
 */
export function useLeftAxis(
  yScale: (yValue: number) => number,
  yTicks: number[],
  display?: Display,
  durationUnit?: TimeDurations | undefined,
) {
  return useMemo(
    () => getYAxisValues(yScale, yTicks, display, durationUnit),
    [yScale, yTicks, display, durationUnit],
  );
}

export const LeftAxis: React.FC<Props> = ({
  height,
  ruler = false,
  rulerLength = 8,
  spacing = defaultSpacing,
  yValues,
}) => (
  <GuidelineGroup
    data-component="LeftAxis"
    transform={`translate(${-spacing.left}, 0)`}
    width={spacing.left}
    height={height}
  >
    {ruler && (
      <line x1={spacing.left} x2={spacing.left} y2={height} stroke="black" strokeWidth={1} />
    )}
    {yValues?.map(([t, o]) => (
      <React.Fragment key={t}>
        {ruler && <line x1={spacing.left - rulerLength} y1={o} x2={spacing.left} y2={o} />}
        <text
          data-testid="left-axis-text"
          alignmentBaseline="middle"
          transform={`translate(0, ${o})`}
          className={css.leftAxisText}
        >
          {t}
        </text>
      </React.Fragment>
    ))}
  </GuidelineGroup>
);
